/**
 * Org invitation (`/api/admin/organizations/:id/invites`) +
 * public preview (`/api/public/invites/:token`) request + response schemas
 * (issue #276 — multi-tenancy Phase 5).
 *
 * Endpoints shipped in Phase 5 (consumed by the SPA's OrganizationDetailPage
 * + SignupPage):
 *
 *   POST   /api/admin/organizations/:id/invites               — create invite
 *   GET    /api/admin/organizations/:id/invites               — list pending
 *   DELETE /api/admin/organizations/:id/invites/:invitationId — revoke
 *   GET    /api/public/invites/:token                         — preview (no auth)
 *
 * The create endpoint has TWO success shapes — discriminated by `kind`:
 *
 *   - `kind: "existing-user"` — the email already mapped to a User row.
 *     The server inserted an OrganizationMembership directly; no token is
 *     issued, no email is needed. SPA refreshes the members list.
 *
 *   - `kind: "invitation"`    — no User row for that email yet.  The server
 *     inserted an OrganizationInvitation row + returned the URL the inviter
 *     should send to the invitee manually (no transport in Phase 5).
 *
 * The signup endpoint accepts an optional `inviteToken` (declared on the
 * shared `signupSchema` in `./auth.ts`, NOT here) which the SPA threads
 * through when the URL carries `?invite=<token>`.
 */

import { z } from "zod";

/**
 * Body schema for `POST /api/admin/organizations/:id/invites`.
 *
 * Email is the only field — the role is fixed at MEMBER for Phase 5
 * (matches the issue spec; promoting to OWNER goes through the existing
 * `PATCH /api/admin/organizations/:id/members/:userId` after acceptance).
 *
 * Normalisation lives at the wire boundary so the service / repo never
 * see un-lowercased values: trim → lowercase → email-shape.
 */
export const createInviteSchema = z
  .object({
    email: z.string().trim().toLowerCase().email(),
  })
  .strict();
export type CreateInviteInput = z.infer<typeof createInviteSchema>;

/**
 * One pending invitation row returned by
 * `GET /api/admin/organizations/:id/invites`.
 *
 * `invitedBy*` is best-effort: the FK is intentionally absent on the
 * Prisma model so a hard-deleted inviter doesn't strand historical
 * invitations. Reads tolerate a null result by emitting
 * `invitedByEmail: null` / `invitedByName: null`.
 */
export type OrgInvitationResponse = {
  id: string;
  organizationId: string;
  email: string;
  invitedByUserId: string;
  invitedByEmail: string | null;
  invitedByName: string | null;
  invitedAt: string;
  expiresAt: string;
  /**
   * Pre-built URL the inviter can copy + send manually. Shape:
   * `${PUBLIC_BASE_URL}/signup?invite=<token>`. Surfaced on the list
   * endpoint so the operator UI can re-display the link for any
   * pending invitation without a second round-trip.
   */
  inviteUrl: string;
};

/**
 * Successful create response — discriminated on `kind`.
 *
 *   - `existing-user`: the invitee already has a User row; an
 *     OrganizationMembership was inserted directly. No token issued.
 *   - `invitation`:    no User row yet; an OrganizationInvitation row
 *     was created. The SPA shows `inviteUrl` in a copy dialog.
 */
export type CreateInviteResponse =
  | {
      kind: "existing-user";
      membership: {
        userId: string;
        email: string;
        organizationId: string;
        role: "MEMBER";
        joinedAt: string;
      };
    }
  | {
      kind: "invitation";
      invitation: OrgInvitationResponse;
    };

/**
 * Response shape for `GET /api/admin/organizations/:id/invites`. Plain
 * array under `data` — every member needs the full pending list to render
 * the "Pending invitations" table on the org detail page, no pagination.
 */
export type ListInvitationsResponse = {
  invitations: OrgInvitationResponse[];
};

/**
 * Response shape for `GET /api/public/invites/:token`. Deliberately minimal:
 * only the org name + invited email leak through. No invitation id, no
 * inviter info, no expiry — those would let an attacker probe the
 * back-of-house structure without adding any value for the legitimate
 * invitee, who already trusts the invite URL they were sent.
 *
 * 404 for missing / expired / already-accepted tokens — the controller
 * does not distinguish the three cases to keep the surface privacy-
 * preserving.
 */
export type PublicInvitePreviewResponse = {
  orgName: string;
  email: string;
};
